import { msg } from '@lingui/core/macro';
import { toastApplicationSaveError } from 'modules/teams/ui/utils';
import { useCallback, useState } from 'react';

export type ApplicationFilterAction = 'accept' | 'decline';

/**
 * Пара «Принять» / «Отклонить» над списком заявок (оба флоу фильтров, IMGM-1561).
 *
 * Нажатую кнопку запирает её промис (кит); `busyAction` — не замок, а «гасим вторую кнопку пары», пока работает
 * первая. Отказ — тостом с причиной (`toastApplicationSaveError` глушит повтор с кодом 15). Тоста «Игроки отклонены»
 * больше нет: он показывался только на truthy-результате `onDecline`, а тот всегда `void` — не появлялся ни разу.
 */
export function useApplicationFilterActions(onAccept?: () => Promise<void> | void, onDecline?: () => Promise<void> | void) {
  const [busyAction, setBusyAction] = useState<ApplicationFilterAction | null>(null);

  const runAction = useCallback(
    async (action: ApplicationFilterAction) => {
      const handler = action === 'accept' ? onAccept : onDecline;
      if (!handler) return;
      setBusyAction(action);
      try {
        await handler();
      } catch (error) {
        toastApplicationSaveError(error, action === 'accept' ? msg`Не удалось принять игроков` : msg`Не удалось отклонить игроков`);
      } finally {
        setBusyAction(null);
      }
    },
    [onAccept, onDecline],
  );

  const onClickAccept = useCallback(() => runAction('accept'), [runAction]);
  const onClickDecline = useCallback(() => runAction('decline'), [runAction]);

  return { busyAction, onClickAccept, onClickDecline };
}
